import React, { useState } from "react";
import { Star, Calendar, Check, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import jsCookie from "js-cookie";
import { confirmContextPreview } from "./action";

/* eslint-disable react/prop-types */
interface NewsletterContextPreviewProps {
  response: any;
   onConfirm?: () => void; 
}

const NewsletterContextPreview: React.FC<NewsletterContextPreviewProps> = ({ response,onConfirm }) => {
  const [searchParams] = useSearchParams();
  const userId = jsCookie.get("user_id");
  const threadId = `${searchParams.get("threadId")}`;
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!userId || !threadId) {
    return <div>User ID is not available.</div>;
  }

  if (!response || Object.keys(response).length === 0) {
    return <div className="p-4 text-sm text-gray-600">No context preview available.</div>;
  }

  // Handle nested context_preview structure
  const contextData = response.context_preview || response;

  const headline = contextData.headline?.data || "";
  const headlineTitle = contextData.headline?.title || "Headline";
  const event = contextData.event_details?.data || "";
  const eventTitle = contextData.event_details?.title || "Event Details";
  const keyPoints = contextData.key_points?.data || "";
  const cta = contextData.cta?.data || "";
  const confidence = contextData.confidence_meter?.data || [];
  const confidenceTitle = contextData.confidence_meter?.title || "Confidence Meter";

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const result = await confirmContextPreview(userId, threadId, contextData);
      if (result.success) {
        setIsConfirmed(true);
        onConfirm?.();
      }
    } catch (error) {
      console.error("Failed to confirm context preview:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isConfirmed) {
    return (
      <div className="w-full py-6 px-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-3 justify-center">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-900">Context Confirmed!</h3>
            <p className="text-sm text-green-700 mt-1">You can move ahead to the next step.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller p-3">
      <div className="mb-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Star className="w-2 h-2" />
          <span className="font-medium">{headlineTitle}</span>
        </div>
        <h2 className="font-semibold text-gray-900 leading-snug text-sm">{headline}</h2>
      </div>

      {/* <div className="mb-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Calendar className="w-2 h-2" />
          <span className="font-medium">{eventTitle}</span>
        </div>
        <h2 className="font-semibold text-gray-900 leading-snug text-sm">{event}</h2>
      </div> */}

      <div className="mb-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Check className="w-2 h-2" />
          <span className="font-medium">Key Points</span>
        </div>
        <h2 className="font-semibold text-gray-900 leading-snug text-sm">{keyPoints}</h2>
      </div>

      <div className="mb-3">
        <div className="text-xs text-gray-500 mb-1">CTA</div>
        <button className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-flex items-center gap-2 text-sm">
          <span>{cta || "Action"}</span>
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-800">{confidenceTitle}</div>
        </div>

        <div className="space-y-3">
          {Array.isArray(confidence) &&
            confidence.map((c: any, idx: number) => {
              const pct =
                typeof c.confidence === "string"
                  ? parseInt(c.confidence.replace("%", ""))
                  : Number(c.confidence) || 0;
              return (
                <div key={idx} className="flex items-center">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-sm font-medium text-gray-800 truncate">{c.title}</div>
                      <div className="text-xs text-gray-500 bg-gray-200 rounded-full px-1">
                        {c.tag}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded h-1 overflow-hidden">
                      <div className="bg-green-400 h-1 rounded" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="w-12 text-right text-xs font-semibold text-gray-800">{pct}%</div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="flex items-center justify-start gap-4 mt-3">
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="px-2 py-1 bg-yellow-400 text-white rounded-md font-semibold hover:brightness-95 text-sm"
        >
          {isLoading ? "Confirming..." : "Confirm content"}
        </button>
      </div>
    </div>
  );
};

export default NewsletterContextPreview;
